import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';
import { Mic, Volume2, MessageSquare, AlertCircle, Play, Square, Languages, ArrowLeftRight, Globe, Settings, Gauge, X, HelpCircle, Loader2, User, ShieldCheck, Link, Copy, Phone, PhoneOff, Users, Search } from 'lucide-react';
import { decode, decodeAudioData, createBlob } from './audio-utils';
import pcmProcessorUrl from './src/audio/pcm-processor.js?url';
import Visualizer from './components/Visualizer';
import Peer, { DataConnection, MediaConnection } from 'peerjs';

// Types
interface TranscriptionTurn {
  id: string;
  role: 'user' | 'model';
  text: string;              // Original text (what was spoken)
  translation?: string;      // Translated text
  sourceLang?: string;       // Language of original text
}

const LANGUAGES = [
  { code: 'Korean', label: '한국어', flag: '🇰🇷' },
  { code: 'English', label: 'English', flag: '🇺🇸' },
  { code: 'Japanese', label: '日本語', flag: '🇯🇵' },
  { code: 'Vietnamese', label: 'Tiếng Việt', flag: '🇻🇳' },
];

const VOICES = [
  { name: 'Kore', desc: '여성/맑음' },
  { name: 'Aoede', desc: '여성/부드러움' },
  { name: 'Zephyr', desc: '남성/중성적' },
  { name: 'Puck', desc: '남성/쾌활함' },
  { name: 'Charon', desc: '남성/안정적' },
  { name: 'Fenrir', desc: '남성/깊은저음' },
];

const App: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcriptions, setTranscriptions] = useState<TranscriptionTurn[]>([]);
  const [isModelSpeaking, setIsModelSpeaking] = useState(false);

  const [streamingInput, setStreamingInput] = useState('');
  const [streamingOutput, setStreamingOutput] = useState('');

  const [error, setError] = useState<string | null>(null);

  const [sourceLang, setSourceLang] = useState(LANGUAGES[0]); // Korean
  const [targetLang, setTargetLang] = useState(LANGUAGES[3]); // Vietnamese
  const [selectedVoice, setSelectedVoice] = useState('Charon'); // 남성/안정적
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0); // Default to 1.0x

  // Explanation popup state
  const [selectedSentence, setSelectedSentence] = useState<TranscriptionTurn | null>(null);
  const [explanation, setExplanation] = useState<string>('');
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);

  // Remote mode states
  const [mode, setMode] = useState<'solo' | 'remote'>('solo');
  const [roomId, setRoomId] = useState<string>('');
  const [isHost, setIsHost] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [inviteLink, setInviteLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [myGender, setMyGender] = useState<'male' | 'female'>('male');
  const [remoteStreamReady, setRemoteStreamReady] = useState(false);
  const peerRef = useRef<Peer | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const autoStartedRef = useRef(false);

  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const inputAnalyzerRef = useRef<AnalyserNode | null>(null);
  const outputAnalyzerRef = useRef<AnalyserNode | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const sessionRef = useRef<any>(null);
  const isSessionReadyRef = useRef(false);
  const nextStartTimeRef = useRef<number>(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const transcriptionEndRef = useRef<HTMLDivElement>(null);

  const inputAccumulatorRef = useRef('');
  const outputAccumulatorRef = useRef('');

  // Use a ref to always have the latest speed inside audio processing callbacks
  const speedRef = useRef(playbackSpeed);
  useEffect(() => {
    speedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  // Update speaking state based on audio context time
  useEffect(() => {
    let interval: number;
    if (isConnected) {
      interval = window.setInterval(() => {
        if (outputAudioContextRef.current) {
          const isSpeaking = outputAudioContextRef.current.currentTime < nextStartTimeRef.current;
          setIsModelSpeaking(isSpeaking);
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isConnected]);

  useEffect(() => {
    transcriptionEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcriptions, streamingInput, streamingOutput]);

  // Check URL for room ID on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const room = params.get('room');
    if (room) {
      setRoomId(room);
      setMode('remote');
      setIsHost(false);
    }
  }, []);

  // Auto-start conversation when peer connects in Remote mode (only once)
  useEffect(() => {
    if (mode === 'remote' && peerConnected && remoteStreamReady && !isConnected && !isConnecting && !autoStartedRef.current) {
      autoStartedRef.current = true;
      console.log('[Auto] Peer connected and stream ready, starting conversation (once)');
      startConversation();
    }
  }, [peerConnected, mode, isConnected, isConnecting, remoteStreamReady]);

  // Create room as host
  const createRoom = useCallback(() => {
    const newRoomId = `intervoice-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    setRoomId(newRoomId);
    setIsHost(true);
    setMode('remote');

    const link = `${window.location.origin}${window.location.pathname}?room=${newRoomId}`;
    setInviteLink(link);
  }, []);

  // Copy invite link
  const copyInviteLink = useCallback(() => {
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }, [inviteLink]);

  // Initialize PeerJS and connect
  const initializePeer = useCallback(async () => {
    if (!roomId) return;

    // Cleanup existing peer first
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }

    // Small delay for PeerJS server cleanup
    await new Promise(resolve => setTimeout(resolve, 300));

    // Get local audio stream
    let localStream: MediaStream;
    try {
      localStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      localStreamRef.current = localStream;
    } catch (err) {
      setError('Microphone permission required');
      return;
    }

    // Host uses roomId so guests can find them
    const peer = new Peer(isHost ? roomId : undefined);
    peerRef.current = peer;

    // Store remote audio stream and trigger auto-start
    const handleRemoteStream = (stream: MediaStream) => {
      remoteStreamRef.current = stream;
      setRemoteStreamReady(true);
      console.log('[Peer] Remote stream received, triggering auto-start');
      // Note: Remote audio is not played directly - it goes through Gemini for translation
    };

    peer.on('open', (id) => {
      console.log('[Peer] Connected with ID:', id);
      if (!isHost) {
        // Guest: connect and call host
        peer.connect(roomId);
        const call = peer.call(roomId, localStream);
        console.log('[Peer] Guest calling host...');
        call.on('stream', (remoteStream) => {
          console.log('[Peer] Guest received host stream');
          remoteStreamRef.current = remoteStream;
          handleRemoteStream(remoteStream);
          setPeerConnected(true);
        });
      }
    });

    peer.on('connection', (conn) => {
      console.log('[Peer] Data connection from:', conn.peer);
      // Host calls guest back
      if (isHost) {
        const call = peer.call(conn.peer, localStream);
        console.log('[Peer] Host calling guest...');
        call.on('stream', (remoteStream) => {
          console.log('[Peer] Host received guest stream');
          remoteStreamRef.current = remoteStream;
          handleRemoteStream(remoteStream);
          setPeerConnected(true);
        });
      }
    });

    peer.on('call', async (call) => {
      console.log('[Peer] Incoming call, answering...');
      call.answer(localStream);
      call.on('stream', (remoteStream) => {
        console.log('[Peer] Call answered, received stream');
        remoteStreamRef.current = remoteStream;
        handleRemoteStream(remoteStream);
        setPeerConnected(true);
      });
    });

    peer.on('error', (err) => {
      console.error('[Peer] Error:', err);
      setError(`Peer error: ${err.message}`);
    });

    // Detect when peer connection is closed - only log, don't cleanup
    // WebRTC audio continues even without signaling server
    peer.on('close', () => {
      console.log('[Peer] Peer object closed');
      // Don't cleanup here - audio might still be working
    });

    peer.on('disconnected', () => {
      console.log('[Peer] Disconnected from signaling server (audio still works)');
      // Don't cleanup - WebRTC audio connection is separate from signaling
      // Try to reconnect to signaling server
      peer.reconnect();
    });
  }, [roomId, isHost]);

  // Disconnect peer and cleanup audio
  const disconnectPeer = useCallback(() => {
    // Stop remote audio
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current = null;
    }
    // Stop local stream tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    // Stop remote stream tracks
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach(track => track.stop());
      remoteStreamRef.current = null;
    }
    // Destroy peer
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }
    setPeerConnected(false);
    setRemoteStreamReady(false);
    autoStartedRef.current = false;
    setRoomId('');
    setInviteLink('');
    setMode('solo');
    setIsHost(false);
    console.log('[Peer] Disconnected and cleaned up');
  }, []);

  const stopAudio = useCallback(() => {
    if (sessionRef.current) {
      try {
        sessionRef.current.close();
      } catch (e) {
        console.warn('[Gemini] Error closing session:', e);
      }
      sessionRef.current = null;
    }
    isSessionReadyRef.current = false;
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    if (inputAudioContextRef.current) {
      inputAudioContextRef.current.close();
      inputAudioContextRef.current = null;
    }
    if (outputAudioContextRef.current) {
      outputAudioContextRef.current.close();
      outputAudioContextRef.current = null;
    }
    sourcesRef.current.forEach(source => {
      try { source.stop(); } catch (e) { }
    });
    sourcesRef.current.clear();
    nextStartTimeRef.current = 0;
    setIsConnected(false);
    setIsConnecting(false);
    setIsModelSpeaking(false);
    setStreamingInput('');
    setStreamingOutput('');
  }, []);

  const startConversation = async () => {
    if (isConnected || isConnecting) return;
    setError(null);
    setIsConnecting(true);

    try {
      const apiKey = process.env.API_KEY || process.env.GEMINI_API_KEY;
      if (!apiKey) {
        setError('Gemini API Key is missing. Please check your .env.local file.');
        setIsConnecting(false);
        return;
      }
      const ai = new GoogleGenAI({ apiKey });

      const isRemoteMode = mode === 'remote' && peerConnected && remoteStreamRef.current;
      const inputCtx = new (window.AudioContext || (window as any).webkitAudioContext)(
        isRemoteMode ? undefined : { sampleRate: 16000 }
      );
      const outputCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      inputAudioContextRef.current = inputCtx;
      outputAudioContextRef.current = outputCtx;

      const inputAnalyzer = inputCtx.createAnalyser();
      const outputAnalyzer = outputCtx.createAnalyser();
      inputAnalyzer.fftSize = 256;
      outputAnalyzer.fftSize = 256;
      inputAnalyzerRef.current = inputAnalyzer;
      outputAnalyzerRef.current = outputAnalyzer;

      let stream: MediaStream;
      if (isRemoteMode) {
        stream = remoteStreamRef.current!;
        if (stream.getAudioTracks().length === 0 || !stream.getAudioTracks()[0]?.enabled) {
          setError('Remote audio stream not available');
          setIsConnecting(false);
          return;
        }
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 16000 }
        });
      }

      const systemInstruction = `You are a real-time interpreter between ${sourceLang.code} and ${targetLang.code}.
STRICT RULES:
1. ONLY translate when you hear CLEAR speech in ${sourceLang.code} or ${targetLang.code}.
2. If ${sourceLang.code} is spoken, translate to ${targetLang.code}. If ${targetLang.code} is spoken, translate to ${sourceLang.code}.
3. Output ONLY the translation. No greetings, no explanations, no "Translation:"
4. DO NOT repeat previous translations.
5. DO NOT generate content when there is silence.
6. If you hear noise, breathing, coughing, or unclear audio, stay COMPLETELY SILENT.
7. DO NOT hallucinate or invent speech that wasn't spoken.
8. Never respond to silence with words like "um", "ah", or filler content.
9. Keep translations brief and natural.
10. CRITICAL: Only respond when NEW, CLEAR speech is detected.`;

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.0-flash-exp',
        callbacks: {
          onopen: async () => {
            if (inputCtx.state === 'closed') {
              console.log('[Gemini] AudioContext closed before onopen, aborting.');
              return;
            }
            setIsConnected(true);
            setIsConnecting(false);

            try {
              await inputCtx.audioWorklet.addModule(pcmProcessorUrl);
            } catch (err) {
              console.error('[Gemini] Failed to load audio worklet:', err);
              setError('Failed to load audio processor');
              stopAudio();
              return;
            }

            if (inputCtx.state === 'closed') return;

            const source = inputCtx.createMediaStreamSource(stream);
            const workletNode = new AudioWorkletNode(inputCtx, 'pcm-processor');
            scriptProcessorRef.current = workletNode as any;

            workletNode.port.onmessage = (event) => {
              if (outputAudioContextRef.current && outputAudioContextRef.current.currentTime < nextStartTimeRef.current) return;
              if (isSessionReadyRef.current && sessionRef.current) {
                try {
                  const inputData = event.data;
                  const pcmBlob = createBlob(inputData, inputCtx.sampleRate);
                  const result = sessionRef.current.sendRealtimeInput({ media: pcmBlob });
                  if (result && typeof result.catch === 'function') {
                    result.catch((e: any) => { return; });
                  }
                } catch (e: any) {
                  isSessionReadyRef.current = false;
                }
              }
            };

            source.connect(inputAnalyzer);
            source.connect(workletNode);
            workletNode.connect(inputCtx.destination);

            sessionPromise.then(session => {
              sessionRef.current = session;
              isSessionReadyRef.current = true;
            }).catch(err => {
              if (!isSessionReadyRef.current) return;
              console.error('[Gemini] Session error:', err);
              setError('Failed to establish session');
              stopAudio();
            });
          },
          onmessage: async (message: LiveServerMessage) => {
            if (message.serverContent?.outputTranscription) {
              outputAccumulatorRef.current += message.serverContent.outputTranscription.text;
              setStreamingOutput(outputAccumulatorRef.current);
            } else if (message.serverContent?.inputTranscription) {
              inputAccumulatorRef.current += message.serverContent.inputTranscription.text;
              setStreamingInput(inputAccumulatorRef.current);
            }

            if (message.serverContent?.turnComplete) {
              const finalInput = inputAccumulatorRef.current.trim();
              const finalOutput = outputAccumulatorRef.current.trim();
              if (finalInput) {
                const detectLanguage = (text: string): string => {
                  if (/[\uAC00-\uD7AF\u1100-\u11FF]/.test(text)) return 'Korean';
                  if (/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(text)) return 'Japanese';
                  if (/[ăâđêôơưàảãáạằẳẵắặầẩẫấậèẻẽéẹềểễếệìỉĩíịòỏõóọồổỗốộờởỡớợùủũúụừửữứựỳỷỹýỵ]/i.test(text)) return 'Vietnamese';
                  if (/[\u4E00-\u9FFF]/.test(text)) return 'Chinese';
                  return 'English';
                };
                setTranscriptions(prev => [...prev, {
                  id: `turn-${Date.now()}`,
                  role: 'user',
                  text: finalInput,
                  translation: finalOutput,
                  sourceLang: mode === 'remote' ? 'Remote' : detectLanguage(finalInput)
                }]);
              }
              inputAccumulatorRef.current = '';
              outputAccumulatorRef.current = '';
              setStreamingInput('');
              setStreamingOutput('');
            }

            const base64Audio = message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
            if (base64Audio && outputAudioContextRef.current) {
              const ctx = outputAudioContextRef.current;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
              const audioBuffer = await decodeAudioData(decode(base64Audio), ctx, 24000, 1);
              const source = ctx.createBufferSource();
              source.buffer = audioBuffer;
              source.playbackRate.value = speedRef.current;
              source.connect(outputAnalyzerRef.current!);
              outputAnalyzerRef.current!.connect(ctx.destination);
              source.addEventListener('ended', () => sourcesRef.current.delete(source));
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += (audioBuffer.duration / speedRef.current);
              sourcesRef.current.add(source);
            }

            if (message.serverContent?.interrupted) {
              sourcesRef.current.forEach(s => { try { s.stop(); } catch (e) { } });
              sourcesRef.current.clear();
              nextStartTimeRef.current = 0;
            }
          },
          onerror: (err) => {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes('CLOSING') || msg.includes('CLOSED') || msg.includes('aborted')) {
              stopAudio();
              return;
            }
            console.error(err);
            setError('Connection failed. Retrying...');
            stopAudio();
          },
          onclose: () => stopAudio(),
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: selectedVoice } } },
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          systemInstruction: systemInstruction
        }
      });
    } catch (err) {
      console.error('[Gemini] Init error:', err);
      setError('Check microphone permissions.');
      setIsConnecting(false);
    }
  };

  const requestExplanation = async (turn: TranscriptionTurn) => {
    setSelectedSentence(turn);
    setExplanation('');
    setIsLoadingExplanation(true);
    try {
      const apiKey = process.env.API_KEY || process.env.GEMINI_API_KEY;
      if (!apiKey) {
        setExplanation('API Key missing.');
        setIsLoadingExplanation(false);
        return;
      }
      const ai = new GoogleGenAI({ apiKey });
      const originalLang = turn.sourceLang || sourceLang.code;
      const explanationLang = originalLang === sourceLang.code ? targetLang.code : sourceLang.code;
      const response = await ai.models.generateContent({
        model: 'gemini-2.0-flash-exp',
        contents: `Explain "${turn.text}" in ${explanationLang}`
      });
      setExplanation(response.text || 'No explanation found.');
    } catch (error) {
      setExplanation('Error fetching explanation.');
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-300 flex flex-col items-center p-2 md:p-4 font-sans leading-tight">
      <div className="max-w-4xl w-full flex flex-col gap-2">
        <header className="flex flex-col items-center gap-0.5 mb-1">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-600 rounded-lg shadow-lg shadow-indigo-500/10"><Languages className="w-4 h-4 text-white" /></div>
            <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-purple-400">Gemini Live Interpreter</h1>
          </div>
        </header>

        <div className="flex justify-center gap-2 mb-2">
          <button onClick={() => { setMode('solo'); disconnectPeer(); }} className={`flex-1 max-w-[200px] py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${mode === 'solo' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-zinc-800 text-zinc-500'}`}><Mic className="w-4 h-4" /> Solo Mode</button>
          <button onClick={() => setMode('remote')} className={`flex-1 max-w-[200px] py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${mode === 'remote' ? 'bg-emerald-600 text-white shadow-lg' : 'bg-zinc-800 text-zinc-500'}`}><Users className="w-4 h-4" /> Remote Mode</button>
        </div>

        {mode === 'solo' && (
          <div className="bg-zinc-900/80 border border-zinc-800 p-2 rounded-xl flex flex-wrap items-center justify-center gap-4 shadow-xl backdrop-blur-md">
            <select value={sourceLang.code} onChange={(e) => setSourceLang(LANGUAGES.find(l => l.code === e.target.value)!)} disabled={isConnected || isConnecting} className="bg-indigo-950/30 border border-indigo-500/30 rounded-md px-1.5 py-0.5 text-[11px] outline-none">{LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}</select>
            <ArrowLeftRight className="w-3 h-3 text-zinc-700" />
            <select value={targetLang.code} onChange={(e) => setTargetLang(LANGUAGES.find(l => l.code === e.target.value)!)} disabled={isConnected || isConnecting} className="bg-emerald-950/30 border border-emerald-500/30 rounded-md px-1.5 py-0.5 text-[11px] outline-none">{LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}</select>
            <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)} disabled={isConnected || isConnecting} className="bg-zinc-800 border border-zinc-700 rounded-md px-1.5 py-0.5 text-[11px] outline-none">{VOICES.map(v => <option key={v.name} value={v.name}>{v.name} ({v.desc})</option>)}</select>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-4 flex flex-col gap-2">
            <div className="bg-zinc-900/50 border border-zinc-800 p-3 rounded-2xl flex flex-col gap-3">
              <div className="space-y-2">
                <Visualizer isActive={isConnected && !isModelSpeaking} analyzer={inputAnalyzerRef.current || undefined} color="#6366f1" />
                <Visualizer isActive={isConnected} analyzer={outputAnalyzerRef.current || undefined} color="#a855f7" />
              </div>
              <button onClick={isConnected ? stopAudio : startConversation} disabled={isConnecting} className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${isConnected ? 'bg-zinc-100 text-black' : 'bg-indigo-600 text-white'} disabled:opacity-50`}>
                {isConnecting ? <Loader2 className="animate-spin h-3 w-3" /> : isConnected ? <><Square className="w-3 h-3 fill-current" /> Stop</> : <><Play className="w-3 h-3 fill-current" /> Start Live</>}
              </button>
              {error && <div className="text-[9px] text-red-400 bg-red-500/10 p-1.5 rounded-lg border border-red-500/20">{error}</div>}
            </div>
          </div>

          <div className="lg:col-span-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl flex flex-col h-[400px] overflow-hidden">
            <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
              {transcriptions.map((turn) => (
                <div key={turn.id} onClick={() => requestExplanation(turn)} className={`p-2 rounded-lg cursor-pointer hover:bg-white/5 transition-all ${turn.role === 'user' ? 'bg-indigo-900/20' : 'bg-emerald-900/20'}`}>
                  <p className="text-[11px] font-bold text-zinc-500 mb-0.5">{turn.sourceLang}</p>
                  <p className="text-[11px] text-zinc-300">{turn.text}</p>
                  {turn.translation && <p className="text-[11px] text-zinc-400 mt-1 italic">{turn.translation}</p>}
                </div>
              ))}
              {streamingInput && <div className="p-2 text-[10px] italic opacity-50">{streamingInput}</div>}
              {streamingOutput && <div className="p-2 text-[10px] italic opacity-50 text-indigo-400">{streamingOutput}</div>}
              <div ref={transcriptionEndRef} />
            </div>
          </div>
        </div>

        {selectedSentence && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSentence(null)}>
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-md w-full p-4 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-zinc-300 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-indigo-400" /> Explanation</h3>
                <button onClick={() => setSelectedSentence(null)} className="p-1 hover:bg-zinc-800 rounded-lg"><X className="w-4 h-4 text-zinc-500" /></button>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-3 mb-3 border border-zinc-700/50">
                <p className="text-sm text-zinc-200">{selectedSentence.text}</p>
              </div>
              <div className="bg-indigo-900/20 rounded-xl p-3 border border-indigo-500/20">
                {isLoadingExplanation ? <Loader2 className="w-4 h-4 animate-spin text-indigo-400" /> : <p className="text-sm text-zinc-300 whitespace-pre-wrap">{explanation}</p>}
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 2px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #18181b; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default App;
